import { useQuery } from '@apollo/client/react'
import { Icon, useTranslate } from '@athena/ui'
import { Outlet } from '@tanstack/react-router'

import { MeDocument, StudentConnectionRequestsDocument } from '../graphql/generated/operations.js'

import { AppShell, type NavEntry } from './AppShell.js'

/**
 * The chrome for the student screens that want it.
 *
 * A level below StudentLayout rather than inside it, so the attempt runner can
 * stay outside: a practice in progress still holds the question and nothing
 * else. `me` is read from the cache StudentLayout has already filled.
 *
 * The request inbox shares this navigation; its count uses the same cached
 * query as the inbox and refreshes after each response.
 */
export function StudentShell() {
  const t = useTranslate()
  const { data } = useQuery(MeDocument)
  const viewer = data?.me
  const { data: requests } = useQuery(StudentConnectionRequestsDocument, {
    skip: !viewer?.student || !viewer.onboarding.complete,
    fetchPolicy: 'cache-and-network',
    pollInterval: 15000,
  })
  const pending = requests?.studentConnectionRequests.length ?? 0

  const nav: NavEntry[] = [
    { to: '/$language/learn', label: t('navigation.home'), icon: <Icon.LayoutDashboard /> },
    {
      to: '/$language/learn/progress',
      label: t('navigation.progress'),
      icon: <Icon.TrendingUp />,
    },
    {
      to: '/$language/learn/requests',
      label: pending ? t('connections.navCount', [pending]) : t('connections.nav'),
      icon: <Icon.Users />,
    },
    { to: '/$language/learn/history', label: t('navigation.history'), icon: <Icon.FileText /> },
  ]

  if (!viewer?.student) return null

  return (
    <AppShell
      viewer={{
        name: viewer.student.displayName,
        email: viewer.email,
        avatarUrl: viewer.avatarUrl,
      }}
      nav={nav}
      // A student's dashboard is their practice list.
      home="/$language/learn"
    >
      <Outlet />
    </AppShell>
  )
}
